import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, BellRing, Mail } from "lucide-react";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import GooglePlacesAutocomplete from "../components/GooglePlacesAutocomplete";

const input =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest transition";
const label = "block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1";

/** Self-serve CPD intake form — the link texted/emailed when an officer opts
 * out of finishing by voice. Submitting runs the exact same intake pipeline
 * (VA alert, escalation) as a phone call. */
export default function CPDIntakeForm() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    officer_name: "", officer_phone: "", officer_email: "",
    unit_number: "", case_number: "", notes: "", update_channel: "push",
  });
  const [pickup, setPickup] = useState({ address: "" });
  const [dropoff, setDropoff] = useState({ address: "" });

  const [loadError, setLoadError] = useState(null); // "not_found" | "server_error" | null

  const fetchForm = () => {
    setLoading(true);
    setLoadError(null);
    apiClient.get(`/cpd/form/${token}`)
      .then(({ data }) => {
        setForm((f) => ({
          ...f,
          officer_name: data.officer_name || "", officer_phone: data.officer_phone || "",
          officer_email: data.officer_email || "", unit_number: data.unit_number || "",
          case_number: data.case_number || "",
        }));
        if (data.submitted) setSubmitted(true);
      })
      .catch((err) => {
        // Only a real 404 means the link itself is bad — anything else
        // (network hiccup, a transient 5xx) is NOT the same as "invalid or
        // expired" and shouldn't tell the officer to give up on the link.
        setLoadError(err?.response?.status === 404 ? "not_found" : "server_error");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async () => {
    if (!form.officer_phone || !pickup.address || !dropoff.address) {
      toast.error("Callback phone, pickup, and drop-off are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post(`/cpd/form/${token}/submit`, {
        ...form,
        pickup_address: pickup.address, pickup_lat: pickup.lat, pickup_lng: pickup.lng,
        dropoff_address: dropoff.address, dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng,
      });
      setSubmitted(true);
      navigate(`/cpd-track/${data.tracking_token}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (loadError === "not_found") return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="cpd-form-not-found">This link is invalid or has expired.</div>;
  if (loadError === "server_error") {
    return (
      <div className="min-h-screen flex items-center justify-center text-center px-4" data-testid="cpd-form-server-error">
        <div>
          <div className="text-lg font-bold text-slate-800">We're having trouble loading this form right now.</div>
          <p className="text-sm text-slate-500 mt-1 mb-4">This isn't a bad link — just a temporary hiccup. Please try again.</p>
          <button
            onClick={fetchForm}
            data-testid="cpd-form-retry-btn"
            className="px-4 py-2 rounded-md bg-forest text-white text-sm font-bold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center px-4" data-testid="cpd-form-already-submitted">
        <div>
          <ShieldCheck className="mx-auto text-forest mb-3" size={32} />
          <div className="text-lg font-bold text-slate-800">This request has already been submitted.</div>
          <p className="text-sm text-slate-500 mt-1">Dispatch has been alerted — check your phone/email for the tracking link.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-md mx-auto space-y-5" data-testid="cpd-form-page">
        <div className="flex items-center gap-2 text-forest font-bold text-sm uppercase tracking-widest">
          <ShieldCheck size={18} /> Medex CPD Transport Request
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <div>
            <label className={label}>Pickup address *</label>
            <GooglePlacesAutocomplete value={pickup.address} onChange={setPickup} placeholder="Pickup address" testId="cpd-form-pickup" />
          </div>
          <div>
            <label className={label}>Drop-off address *</label>
            <GooglePlacesAutocomplete value={dropoff.address} onChange={setDropoff} placeholder="Drop-off address" testId="cpd-form-dropoff" />
          </div>
          <div>
            <label className={label}>Your name</label>
            <input className={input} value={form.officer_name} onChange={(e) => setForm({ ...form, officer_name: e.target.value })} data-testid="cpd-form-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Unit / badge #</label>
              <input className={input} value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} data-testid="cpd-form-unit" />
            </div>
            <div>
              <label className={label}>Case #</label>
              <input className={input} value={form.case_number} onChange={(e) => setForm({ ...form, case_number: e.target.value })} data-testid="cpd-form-case" />
            </div>
          </div>
          <div>
            <label className={label}>Callback phone *</label>
            <input className={input} value={form.officer_phone} onChange={(e) => setForm({ ...form, officer_phone: e.target.value })} placeholder="(773) 555-0100" data-testid="cpd-form-phone" />
          </div>
          <div>
            <label className={label}>Email</label>
            <input className={input} value={form.officer_email} onChange={(e) => setForm({ ...form, officer_email: e.target.value })} placeholder="you@chicagopolice.gov" data-testid="cpd-form-email" />
          </div>
          <div>
            <label className={label}>Notes for dispatch</label>
            <textarea className={input} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cpd-form-notes" />
          </div>

          <div>
            <label className={label}>How should we send live updates?</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, update_channel: "push" })}
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold flex items-center justify-center gap-1.5 ${form.update_channel === "push" ? "border-forest bg-forest/10 text-forest" : "border-slate-300 text-slate-500"}`}
                data-testid="cpd-form-channel-push"
              >
                <BellRing size={14} /> Push notifications
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, update_channel: "email" })}
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold flex items-center justify-center gap-1.5 ${form.update_channel === "email" ? "border-forest bg-forest/10 text-forest" : "border-slate-300 text-slate-500"}`}
                data-testid="cpd-form-channel-email"
              >
                <Mail size={14} /> Email/SMS only
              </button>
            </div>
          </div>

          <button
            onClick={submit}
            disabled={submitting}
            className="w-full rounded-md bg-forest text-white font-bold py-2.5 text-sm disabled:opacity-50"
            data-testid="cpd-form-submit-btn"
          >
            {submitting ? "Submitting…" : "Submit request"}
          </button>
        </div>
      </div>
    </div>
  );
}
